// client/src/components/pages/LexiStar/Commissioning/RecordImport.jsx
//
// Step 2 of commissioning (LS-09 §5.20): load the customer's own records — trip
// sheets, weighbridge tickets, fuel issues, odometer readings — by template
// import or manual entry. Per kind: the template (columns documented by
// GET /parallel-runs/templates/:kind, downloaded as a CSV with an example
// row), a CSV/XLSX upload (POST /parallel-runs/:id/records:import), and a
// small manual-entry grid that is sent through the same import as a CSV. Every
// import answers with what was taken and an error report per rejected row,
// downloadable as CSV.

import React, { useEffect, useId, useRef, useState } from 'react';
import { FaDownload, FaUpload, FaPlus, FaTrashAlt } from 'react-icons/fa';
import {
  getRecordTemplate,
  importParallelRunRecords,
} from '../../../../services/lexistarAssurance';
import { cardClasses, inputClasses } from '../StatusBoard/atoms';
import { fmtNum } from '../StatusBoard/format';
import { InlineError, buttonClasses, downloadText } from '../ReviewQueue/assuranceAtoms';
import { errorMessage } from '../ReviewQueue/assuranceShared';
import {
  RECORD_KINDS,
  normaliseColumns,
  templateCsv,
  rowsToCsv,
  readImportResult,
  errorReportCsv,
} from './commissioningLogic';

function ImportResult({ result, kind }) {
  if (!result) return null;
  return (
    <div
      role="status"
      className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
    >
      <p className="text-slate-800 tabular-nums">
        <span className="font-semibold">{fmtNum(result.imported)}</span> imported
        {result.rejected > 0 && (
          <>
            {' · '}
            <span className="font-semibold">{fmtNum(result.rejected)}</span> rejected
          </>
        )}
      </p>
      {result.errors.length > 0 && (
        <>
          <table className="w-full mt-2 text-xs" aria-label={`Import errors for ${kind}`}>
            <thead>
              <tr className="text-left text-slate-500">
                <th className="py-1 pr-2 font-semibold">Row</th>
                <th className="py-1 pr-2 font-semibold">Column</th>
                <th className="py-1 font-semibold">Problem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {result.errors.slice(0, 8).map((e, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2 tabular-nums">{e.row ?? '—'}</td>
                  <td className="py-1 pr-2 font-mono">{e.column ?? '—'}</td>
                  <td className="py-1 text-slate-700">{e.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {result.errors.length > 8 && (
            <p className="mt-1 text-xs text-slate-500">
              …and {result.errors.length - 8} more in the report.
            </p>
          )}
          <button
            type="button"
            onClick={() => downloadText(`${kind}-import-errors.csv`, errorReportCsv(result.errors))}
            className={`${buttonClasses.quiet} mt-1`}
          >
            <FaDownload size={10} /> Download the error report
          </button>
        </>
      )}
    </div>
  );
}

function KindCard({ runId, kind, count, disabled, onImported }) {
  const [columns, setColumns] = useState(null);
  const [templateError, setTemplateError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState(false);
  const [draftRows, setDraftRows] = useState([]);
  const fileRef = useRef(null);
  const fileId = useId();

  const ensureColumns = async () => {
    if (columns) return columns;
    try {
      const cols = normaliseColumns(await getRecordTemplate(kind.value));
      setColumns(cols);
      setTemplateError(null);
      return cols;
    } catch (err) {
      setTemplateError(errorMessage(err, 'The template could not be loaded.'));
      return null;
    }
  };

  useEffect(() => {
    if (manual) ensureColumns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manual]);

  const download = async () => {
    const cols = await ensureColumns();
    if (cols?.length) downloadText(`${kind.value}-template.csv`, templateCsv(cols));
  };

  const send = async (file) => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = readImportResult(await importParallelRunRecords(runId, kind.value, file));
      setResult(res);
      onImported?.();
      return res;
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) setResult(readImportResult(data));
      setError(errorMessage(err, 'The import failed; no rows were taken.'));
      return null;
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const sendManual = async () => {
    const cols = await ensureColumns();
    if (!cols?.length || !draftRows.length) return;
    const csv = rowsToCsv(cols, draftRows);
    const file = new File([csv], `${kind.value}-manual.csv`, { type: 'text/csv' });
    const res = await send(file);
    if (res && res.rejected === 0) setDraftRows([]);
  };

  return (
    <section className={`${cardClasses} p-4`} aria-label={kind.label}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">{kind.label}</h3>
          <p className="text-xs text-slate-600">{kind.help}</p>
        </div>
        <span className="text-right">
          <span className="block text-xl font-bold text-slate-900 tabular-nums">
            {fmtNum(count || 0)}
          </span>
          <span className="block text-[11px] text-slate-500">loaded</span>
        </span>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" onClick={download} className={buttonClasses.secondary}>
          <FaDownload size={11} /> Template
        </button>
        <label
          htmlFor={fileId}
          className={`${buttonClasses.primary} cursor-pointer ${disabled || busy ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <FaUpload size={11} /> {busy ? 'Importing…' : 'Import CSV / XLSX'}
        </label>
        <input
          id={fileId}
          ref={fileRef}
          type="file"
          accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="sr-only"
          disabled={disabled || busy}
          onChange={(e) => e.target.files?.[0] && send(e.target.files[0])}
          aria-label={`Import ${kind.label}`}
        />
        <button
          type="button"
          onClick={() => setManual((m) => !m)}
          aria-expanded={manual}
          className={buttonClasses.secondary}
          disabled={disabled}
        >
          <FaPlus size={10} /> Enter by hand
        </button>
      </div>
      <InlineError message={templateError} />
      {manual && columns && (
        <div className="mt-3 space-y-2">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" aria-label={`Enter ${kind.label}`}>
              <thead>
                <tr className="text-left text-slate-500">
                  {columns.map((c) => (
                    <th
                      key={c.name}
                      className="py-1 pr-2 font-semibold whitespace-nowrap"
                      title={c.description}
                    >
                      {c.name}
                      {c.required && <span aria-label="required"> *</span>}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {draftRows.map((r, i) => (
                  <tr key={i}>
                    {columns.map((c) => (
                      <td key={c.name} className="py-1 pr-2">
                        <input
                          className={`${inputClasses} !px-2 !py-1 min-w-[110px]`}
                          value={r[c.name] ?? ''}
                          placeholder={c.example ? String(c.example) : ''}
                          aria-label={`${c.name} row ${i + 1}`}
                          onChange={(e) =>
                            setDraftRows((rows) =>
                              rows.map((x, j) => (j === i ? { ...x, [c.name]: e.target.value } : x))
                            )
                          }
                        />
                      </td>
                    ))}
                    <td>
                      <button
                        type="button"
                        onClick={() => setDraftRows((rows) => rows.filter((_, j) => j !== i))}
                        aria-label={`Remove row ${i + 1}`}
                        className="p-2 text-slate-400 hover:text-slate-700"
                      >
                        <FaTrashAlt size={11} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setDraftRows((rows) => [...rows, {}])}
              className={buttonClasses.quiet}
            >
              <FaPlus size={9} /> Add a row
            </button>
            <button
              type="button"
              onClick={sendManual}
              disabled={busy || !draftRows.length}
              className={buttonClasses.primary}
            >
              Import {draftRows.length} entered row{draftRows.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}
      <InlineError message={error} />
      <ImportResult result={result} kind={kind.value} />
    </section>
  );
}

export default function RecordImport({ runId, counts, disabled, onImported }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Load the customer’s own records for the window. Nothing they send changes LexiStar’s numbers
        — they are what LexiStar is measured against.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {RECORD_KINDS.map((k) => (
          <KindCard
            key={k.value}
            runId={runId}
            kind={k}
            count={counts?.[k.value]}
            disabled={disabled}
            onImported={onImported}
          />
        ))}
      </div>
    </div>
  );
}
